import { useEffect, useRef } from 'react';
import { KeyboardHotkeys } from '@/enums/KeyboardHotkeys';

const normalizeCombo = (combo: string) =>
  combo
    .split('+')
    .map((key) => key.trim())
    .filter(Boolean)
    .sort()
    .join('+');

const getComboFromEvent = (event: KeyboardEvent): string => {
  const parts: string[] = [];

  if (event.ctrlKey) parts.push('Control');
  if (event.shiftKey) parts.push('Shift');
  if (event.altKey) parts.push('Alt');
  if (event.metaKey) parts.push('Meta');

  parts.push(event.code);
  return normalizeCombo(parts.join('+'));
};

export const useKeyboardShortcut = (
  shortcut: KeyboardHotkeys,
  callback: (event: KeyboardEvent) => void,
  shouldTrigger = true,
) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!shouldTrigger) return;

    const normalizedShortcut = normalizeCombo(shortcut);

    const handler = (event: KeyboardEvent) => {
      const pressedCombo = getComboFromEvent(event);

      if (pressedCombo === normalizedShortcut) {
        event.preventDefault();
        callbackRef.current(event);
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [shortcut, shouldTrigger]);
};
